/**
 * Auth service — PKCE-based token management for HS Auth Gateway.
 * Handles login, callback, token storage, refresh, and logout.
 */

const TOKEN_KEY = 'hs_auth_tokens';

interface TokenSet {
  access_token: string;
  refresh_token: string;
  expires_at: number; // epoch ms
}

interface AuthConfig {
  gatewayUrl: string;
  appId: string;
  redirectUri: string;
}

// Populated from environment. The gateway has NO fallback (bug:c6661b1cc362): a production build
// without VITE_AUTH_GATEWAY_URL fails in vite.config.ts, and sign-in refuses an empty one rather
// than sending the browser to localhost.
const config: AuthConfig = {
  gatewayUrl: (import.meta.env.VITE_AUTH_GATEWAY_URL || '').replace(/\/+$/, ''),
  appId: import.meta.env.VITE_AUTH_APP_ID || 'pmo',
  redirectUri: import.meta.env.VITE_AUTH_REDIRECT_URI || `${window.location.origin}/auth/callback`,
};

/** The hs-auth gateway, for a second sign-in to another app (the Ways module, waysAuth.ts). */
export const authGatewayUrl = config.gatewayUrl;

/** The gateway to send the browser to, or an error: never a guessed or local address. */
export function requireGatewayUrl(): string {
  if (!config.gatewayUrl) {
    throw new Error('Sign-in is not configured: this PMO build has no auth gateway (VITE_AUTH_GATEWAY_URL).');
  }
  return config.gatewayUrl;
}

// ─── PKCE Helpers ───────────────────────────────────────────────

export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);

  // crypto.subtle is only available in secure contexts (HTTPS / localhost).
  // Fall back to a pure-JS SHA-256 when running on plain HTTP.
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const hash = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(hash));
  }

  // Pure-JS SHA-256 fallback (RFC 6234)
  const hash = sha256(data);
  return base64UrlEncode(hash);
}

function base64UrlEncode(buffer: Uint8Array): string {
  let binary = '';
  for (const byte of buffer) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ─── Pure-JS SHA-256 fallback (for plain HTTP contexts) ─────────

function sha256(data: Uint8Array): Uint8Array {
  const K: number[] = [
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ];

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const msgLen = data.length;
  const bitLen = msgLen * 8;
  // Pre-processing: pad to 64-byte blocks
  const totalLen = Math.ceil((msgLen + 9) / 64) * 64;
  const msg = new Uint8Array(totalLen);
  msg.set(data);
  msg[msgLen] = 0x80;
  // Length in bits as big-endian 64-bit at end
  const dv = new DataView(msg.buffer);
  dv.setUint32(totalLen - 4, bitLen, false);

  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

  for (let offset = 0; offset < totalLen; offset += 64) {
    const w = new Int32Array(64);
    for (let i = 0; i < 16; i++) w[i] = dv.getInt32(offset + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i-15]>>>0, 7) ^ rotr(w[i-15]>>>0, 18) ^ (w[i-15] >>> 3);
      const s1 = rotr(w[i-2]>>>0, 17) ^ rotr(w[i-2]>>>0, 19) ^ (w[i-2] >>> 10);
      w[i] = (w[i-16] + s0 + w[i-7] + s1) | 0;
    }

    let a=h0,b=h1,c=h2,d=h3,e=h4,f=h5,g=h6,h=h7;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e>>>0,6) ^ rotr(e>>>0,11) ^ rotr(e>>>0,25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) | 0;
      const S0 = rotr(a>>>0,2) ^ rotr(a>>>0,13) ^ rotr(a>>>0,22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h=g; g=f; f=e; e=(d+t1)|0; d=c; c=b; b=a; a=(t1+t2)|0;
    }
    h0=(h0+a)|0; h1=(h1+b)|0; h2=(h2+c)|0; h3=(h3+d)|0;
    h4=(h4+e)|0; h5=(h5+f)|0; h6=(h6+g)|0; h7=(h7+h)|0;
  }

  const out = new Uint8Array(32);
  const ov = new DataView(out.buffer);
  ov.setUint32(0,h0,false); ov.setUint32(4,h1,false); ov.setUint32(8,h2,false); ov.setUint32(12,h3,false);
  ov.setUint32(16,h4,false); ov.setUint32(20,h5,false); ov.setUint32(24,h6,false); ov.setUint32(28,h7,false);
  return out;
}

// ─── Login / Logout ─────────────────────────────────────────────

export async function login(loginHint?: string): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateCodeVerifier(); // reuse for random state

  // Store PKCE verifier and state for callback
  sessionStorage.setItem('hs_auth_code_verifier', codeVerifier);
  sessionStorage.setItem('hs_auth_state', state);

  const params = new URLSearchParams({
    app_id: config.appId,
    redirect_uri: config.redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state,
  });

  if (loginHint) params.set('login_hint', loginHint);

  window.location.href = `${requireGatewayUrl()}/api/auth/authorize?${params.toString()}`;
}

export async function handleCallback(searchParams: URLSearchParams): Promise<boolean> {
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');

  if (error) {
    throw new Error(`Auth error: ${error}`);
  }

  if (!code || !state) return false;

  const savedState = sessionStorage.getItem('hs_auth_state');
  if (state !== savedState) {
    throw new Error('State mismatch — possible CSRF attack');
  }

  const codeVerifier = sessionStorage.getItem('hs_auth_code_verifier');
  if (!codeVerifier) {
    throw new Error('Missing PKCE code verifier');
  }

  const res = await fetch('/api/auth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      code,
      code_verifier: codeVerifier,
      app_id: config.appId,
      // Exactly the redirect_uri sent to /authorize for this sign-in. hs-auth rejects a
      // mismatch (invalid_grant) and will require it outright (PO-59 TS-5,
      // TOKEN_REQUIRE_REDIRECT_URI); the Ways sign-in already sends it.
      redirect_uri: config.redirectUri,
    }),
  });

  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.message || data.error || 'Token exchange failed');
  }

  const tokenData = await res.json();
  storeTokens(tokenData);

  // Clean up
  sessionStorage.removeItem('hs_auth_code_verifier');
  sessionStorage.removeItem('hs_auth_state');

  return true;
}

export function logout(): void {
  const tokens = getStoredTokens();

  if (tokens) {
    // Fire and forget — proxy through backend
    fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: tokens.refresh_token }),
    }).catch(() => {});
  }

  clearTokens();
}

// ─── Token Storage ──────────────────────────────────────────────

function storeTokens(data: { access_token: string; refresh_token: string; expires_in: number }): void {
  const tokenSet: TokenSet = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: Date.now() + data.expires_in * 1000,
  };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenSet));
}

function getStoredTokens(): TokenSet | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// ─── Token Access ───────────────────────────────────────────────

export function getAccessToken(): string | null {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  // Check if token will expire in the next 60 seconds
  if (tokens.expires_at - Date.now() < 60_000) {
    return null; // Force refresh
  }

  return tokens.access_token;
}

export async function getValidAccessToken(): Promise<string | null> {
  const token = getAccessToken();
  if (token) return token;

  // Try refresh
  return refreshAccessToken();
}

export async function refreshAccessToken(): Promise<string | null> {
  const tokens = getStoredTokens();
  if (!tokens?.refresh_token) return null;

  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refresh_token: tokens.refresh_token,
        app_id: config.appId,
      }),
    });

    if (!res.ok) {
      // Only clear tokens on definitive auth failures (401/403).
      // Transient errors (5xx, network) should not destroy the session.
      if (res.status === 401 || res.status === 403) {
        clearTokens();
      }
      return null;
    }

    const data = await res.json();
    storeTokens(data);
    return data.access_token;
  } catch {
    // Network error — don't clear tokens, just return null
    return null;
  }
}

export function isAuthenticated(): boolean {
  return getStoredTokens() !== null;
}

// ─── Proactive Token Refresh ────────────────────────────────────

let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Schedule a background token refresh before the access token expires.
 * Clears any existing timer first. Schedules the refresh for
 * (expires_at - now - 90s) so we refresh 90 seconds before expiry.
 * If the token is already close to expiry, refreshes immediately.
 */
export function startTokenRefreshScheduler(): void {
  stopTokenRefreshScheduler();

  const tokens = getStoredTokens();
  if (!tokens) return;

  const msUntilExpiry = tokens.expires_at - Date.now();
  // Refresh 90 seconds before expiry, minimum 5 seconds from now
  const refreshIn = Math.max(msUntilExpiry - 90_000, 5_000);

  _refreshTimer = setTimeout(async () => {
    try {
      const newToken = await refreshAccessToken();
      if (newToken) {
        // Successfully refreshed — schedule the next one
        startTokenRefreshScheduler();
      }
      // If refresh returned null, tokens were cleared by refreshAccessToken
      // and the next API call will trigger the login redirect.
    } catch {
      // Transient failure — retry in 30 seconds instead of giving up
      _refreshTimer = setTimeout(() => startTokenRefreshScheduler(), 30_000);
    }
  }, refreshIn);
}

/** Stop the background token refresh timer. */
export function stopTokenRefreshScheduler(): void {
  if (_refreshTimer !== null) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
}

// ─── JWT Parsing ────────────────────────────────────────────────

export function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  user_type: string;
  roles: string[];
}

export function getCurrentUserFromToken(): AuthUser | null {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  const payload = parseJwt(tokens.access_token);
  if (!payload) return null;

  return {
    sub: payload.sub as string,
    email: payload.email as string,
    name: payload.name as string,
    user_type: payload.user_type as string,
    roles: (payload.roles as string[]) || [],
  };
}
